// src/features/imprint/composables/useDataCleanup.js

import { useNewImprintStore } from '../stores/useNewImprintStore.js';
import { useSelectionStore } from '../stores/useSelectionStore.js';
import { useCalendarStore } from '../stores/useCalendarStore.js';
import { useDesignOnlineStore } from '@/features/design/stores/useDesignOnlineStore.js';

/**
 * Data Cleanup Composable
 * 
 * Provides centralized methods for clearing stored data across all stores.
 * Used in two scenarios:
 * 1. After successful Add to Cart - clearAllData()
 * 2. When navigating to different product - fullCleanup()
 * 
 * @returns {Object} Cleanup methods
 */
export function useDataCleanup() {
    
    /**
     * Clear all sessionStorage and localStorage data
     * Called after successful Add to Cart
     * 
     * Does NOT reset store state - just clears persistent storage
     * 
     * @returns {boolean} Success status
     */
    function clearAllData() {
        console.log('[useDataCleanup] 🗑️ Clearing all stored data...');
        
        let success = true;
        
        try {
            // 1. Clear New Imprint Store (Start New Imprint + Design Online flow)
            const newImprintStore = useNewImprintStore();
            newImprintStore.clearSessionStorage();
            console.log('[useDataCleanup] ✅ NewImprintStore sessionStorage cleared');
            
        } catch (error) {
            console.error('[useDataCleanup] ❌ Failed to clear NewImprintStore:', error);
            success = false;
        }
        
        try {
            // 2. Clear Selection Store (Color/Size selection)
            const selectionStore = useSelectionStore();
            selectionStore.clearSessionStorage();
            console.log('[useDataCleanup] ✅ SelectionStore sessionStorage cleared');
            
        } catch (error) {
            console.error('[useDataCleanup] ❌ Failed to clear SelectionStore:', error);
            success = false;
        }
        
        try {
            // 3. Clear Calendar Store (localStorage for calendar bundles)
            const calendarStore = useCalendarStore();
            calendarStore.clear();
            console.log('[useDataCleanup] ✅ CalendarStore localStorage cleared');
            
        } catch (error) {
            console.error('[useDataCleanup] ❌ Failed to clear CalendarStore:', error);
            success = false;
        }
        
        try {
            // 4. Clear Design Online Store (sessionStorage + reset state)
            const designOnlineStore = useDesignOnlineStore();
            
            // ✅ CRITICAL: Reset to 'idle' stage to prevent watcher from triggering auto-scroll
            // Watcher in CustomizeProductSection watches for stage = 'preview' to auto-switch tabs
            // Setting to 'idle' prevents unwanted auto-switch after Add to Cart
            designOnlineStore.stage = 'idle';
            
            // Clear sessionStorage
            designOnlineStore.clearStorage();
            
            // Reset all design data fields
            designOnlineStore.tokenId = null;
            designOnlineStore.previewImage = null;
            designOnlineStore.editUrl = null;
            designOnlineStore.pdfPreviewUrl = null;
            designOnlineStore.printReadyFileUrl = null;
            designOnlineStore.isApproved = false;
            designOnlineStore.error = null;
            designOnlineStore.source = null;
            
            console.log('[useDataCleanup] ✅ DesignOnlineStore cleared (stage = idle, no auto-scroll)');
            
        } catch (error) {
            console.error('[useDataCleanup] ❌ Failed to clear DesignOnlineStore:', error);
            success = false;
        }
        
        if (success) {
            console.log('[useDataCleanup] ✅ All data cleared successfully');
        } else {
            console.warn('[useDataCleanup] ⚠️ Some data failed to clear');
        }
        
        return success;
    }
    
    /**
     * Full cleanup - clear storage AND reset stores to initial state
     * Called when navigating to a different product
     * 
     * More aggressive than clearAllData() - resets everything
     * 
     * @returns {boolean} Success status
     */
    function fullCleanup() {
        console.log('[useDataCleanup] 🧹 Full cleanup (storage + reset stores)...');
        
        let success = true;
        
        try {
            // 1. Clear New Imprint Store storage (includes Design Online data)
            const newImprintStore = useNewImprintStore();
            newImprintStore.clearSessionStorage();
            
            // ✅ CRITICAL: Use Pinia $reset() for complete store reset
            // This resets ALL state to initial values (imprints, methodsData, etc.)
            newImprintStore.$reset();
            console.log('[useDataCleanup] ✅ NewImprintStore cleared and reset ($reset)');
            
        } catch (error) {
            console.error('[useDataCleanup] ❌ Failed to cleanup NewImprintStore:', error);
            success = false;
        }
        
        try {
            // 2. Clear Selection Store storage
            const selectionStore = useSelectionStore();
            selectionStore.clearSessionStorage();
            
            // Reset store to initial state
            selectionStore.$reset();
            console.log('[useDataCleanup] ✅ SelectionStore cleared and reset');
            
        } catch (error) {
            console.error('[useDataCleanup] ❌ Failed to cleanup SelectionStore:', error);
            success = false;
        }
        
        try {
            // 3. Clear Calendar Store
            const calendarStore = useCalendarStore();
            calendarStore.clear();
            console.log('[useDataCleanup] ✅ CalendarStore cleared');
            
        } catch (error) {
            console.error('[useDataCleanup] ❌ Failed to cleanup CalendarStore:', error);
            success = false;
        }
        
        try {
            // 4. Clear Design Online Store
            const designOnlineStore = useDesignOnlineStore();
            
            // Use fullReset() which internally sets stage = 'idle'
            designOnlineStore.fullReset();
            
            console.log('[useDataCleanup] ✅ DesignOnlineStore cleared and reset (fullReset)');
            
        } catch (error) {
            console.error('[useDataCleanup] ❌ Failed to cleanup DesignOnlineStore:', error);
            success = false;
        }
        
        if (success) {
            console.log('[useDataCleanup] ✅ Full cleanup completed successfully');
        } else {
            console.warn('[useDataCleanup] ⚠️ Some cleanup failed');
        }
        
        return success;
    }
    
    return {
        clearAllData,      // After Add to Cart
        fullCleanup        // When navigating to different product
    };
}
